import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  Theme,
  DialogProps,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { PartnerForm } from '@/components/hiring'
import {
  PartnerFormData,
  partnerFormSchema,
} from '@/pages/hiring/main-partner/schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { defaultPartnerFormValues } from '@/pages/hiring/main-partner/defaults'
import {
  useReadPartner,
  useCreatePartner,
  useUpdatePartner,
} from '@/api/accounting-dealer'
import { LoadingButton } from '@mui/lab'
import { PartnerPayload } from '@/types/partner'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/helpers'

type OtherPartnersDialogProps = DialogProps & {
  companyKey: string
  partnerKey: string | null
  onClose: () => void
}

export const PartnerFormDialog = ({
  partnerKey,
  companyKey,
  ...props
}: OtherPartnersDialogProps) => {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const queryClient = useQueryClient()

  const { data: partner, isLoading: isLoadingPartner } = useReadPartner(
    {
      companyKey,
      partnerKey: partnerKey || '',
    },
    { enabled: !!partnerKey },
  )

  function handleOnSuccess() {
    props.onClose()
    queryClient.invalidateQueries({
      queryKey: ['partners', companyKey],
    })
  }

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
    values: (partner as PartnerFormData) || defaultPartnerFormValues,
  })

  const { mutateAsync: createPartner, isPending: isCreatingPartner } =
    useCreatePartner({
      onSuccess: () => {
        form.reset()
        handleOnSuccess()
        toast.success('Sócio criado com sucesso')
      },
    })
  const { mutateAsync: updatePartner, isPending: isUpdatingPartner } =
    useUpdatePartner({
      onSuccess: () => {
        handleOnSuccess()
        toast.success('Sócio atualizado com sucesso')
      },
    })

  const handleCreateOrUpdatePartner = (data: PartnerFormData) => {
    if (partnerKey) {
      return updatePartner({
        companyKey: companyKey,
        partnerKey: partnerKey,
        partnerData: { ...data, is_main_partner: false } as PartnerPayload,
      })
    }
    return createPartner({
      companyKey: companyKey,
      partnerData: { ...data, is_main_partner: false } as PartnerPayload,
    })
  }

  return (
    <Dialog
      {...props}
      open={props.open}
      component="form"
      maxWidth="md"
      fullScreen={isMobile}
      onSubmit={form.handleSubmit(handleCreateOrUpdatePartner)}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {partnerKey ? 'Editar Sócio' : 'Adicionar Sócio'}
      </DialogTitle>

      <DialogContent>
        <FormProvider {...form}>
          <PartnerForm isLoading={isLoadingPartner} />
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={props.onClose}>
          Cancelar
        </Button>
        <LoadingButton
          type="submit"
          loading={isCreatingPartner || isUpdatingPartner}
        >
          {partnerKey ? 'Salvar' : 'Cadastrar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
